import { VitalsState, dropPendingLooks, receiveLooks } from "odyssey-shared";
import { deathCosts } from "./death/cost.ts";
import type { DoorPrompt } from "./door-prompt.ts";
import { PAGE_RULES } from "./rules.ts";
import { say } from "../words.ts";
import { flagRefusalLine } from "./hud/flag-toggle.ts";
import { announceDiscovery } from "./hud/discovery.ts";
import { frameMaxHealthPercent } from "./hud/health-penalty.ts";
import { levelUpLine } from "./hud/xp.ts";
import { ownLevelUp } from "./vfx/triggers.ts";
import { countdownOf } from "./hud/maintenance-warning.ts";
import type { JoinHandlers } from "./join.ts";
import { gameReport } from "./report.ts";
import { snapshotReceiver } from "./session-snapshot.ts";
import { worldSounds } from "./session-sounds.ts";
import type { SessionLifetime, SessionParts } from "./session-types.ts";
import type { SessionCrossing } from "./session-crossing.ts";
import type { SessionHandoff } from "./session-handoff.ts";
import { receiveWelcome } from "./session-welcome.ts";
import { playThingEvents } from "./shrine-looks.ts";
import { ownNow } from "./own-clock.ts";
import { PAGE_MAP_LIGHTS, hearSource } from "./world-ui/lighting/map-lights.ts";

/**
 * Makes the handlers of every server frame the join hands on: the welcome and the snapshots (`session-welcome.ts`,
 * `session-snapshot.ts`), the interactions, chat, the bag, the wallet, the bar and the chest, the emotes and emoji,
 * the maintenance schedule, which the loading shell holds too for the strip over its queue window, the page's place
 * in the login queue (P1.4-T164), feedback and moderation, the player's own vitals, effects, progress and combat, the
 * flag's state and switch, the shrines, the map's places and exploration (ADR-100) and the events on things, the looks and the shots, and a frame the page
 * discarded. A close and a silent server are the session's lifetime. The handlers are made once and read the session's
 * state in place, so a frame allocates nothing the handlers add.
 *
 * @param parts The session's parts.
 * @param lifetime The session's own moves.
 * @param handoff The session's moves between maps.
 * @param door The prompt of a door entered with E.
 * @param crossing The page's side of seamless travel, fed each edge view, edgeNear and crossing welcome (ADR-147).
 * @returns The handlers the join is given.
 */
export function frameHandlers(parts: SessionParts, lifetime: SessionLifetime, handoff: SessionHandoff, door: DoorPrompt, crossing: SessionCrossing): JoinHandlers {
  const { options, host, state, connection, fight, ground, looks, levels } = parts;
  const sounds = worldSounds(parts);
  const snapshot = snapshotReceiver(parts, crossing);
  return {
    onWelcome: (welcome) => receiveWelcome(parts, lifetime, handoff, sounds, welcome, crossing),
    onHandoff: (frame) => handoff.begin(frame),
    onEdgeView: (view) => crossing.edgeView(view),
    onEdgeNear: (near) => crossing.edgeNear(near),
    onDoorPrompt: (shown, name) => door.receive(shown, name),
    onDoorRefused: (reason) => host.messageLine.say(say(`door.refused.${reason}`)),
    onSnapshot: snapshot,
    onInteraction: (interaction) => host.interaction(interaction),
    onDialogue: (line) => options.dialogue?.receive(line),
    onDialogueClose: (handle, reason) => options.dialogue?.closed(handle, reason),
    onCompanionNaming: (state) => {
      options.dialogue?.naming(state);
      if (state === "named") {
        options.sounds?.companionAcquired();
      }
    },
    onSky: (sky) => {
      gameReport.lighting.sky = sky;
    },
    onSource: (key, lit) => hearSource(PAGE_MAP_LIGHTS, key, lit),
    onChat: (event) => options.chat?.receive(event),
    onRoundTrip: lifetime.roundTrip,
    onBag: (event) => {
      options.bag?.receive(event);
      if (event.kind !== "refused") {
        options.chest?.bagChanged();
      }
    },
    onWallet: (amounts) => options.wallet?.receive(amounts),
    onAttributes: (event) => (event.kind === "frame" ? options.character?.receive(event.frame) : options.character?.refused(event.reason)),
    onTravel: (event) => options.travel?.receive(event),
    onHarvestState: (event) => options.harvest?.receiveState(event),
    onHarvested: (list) => options.harvest?.receiveList(list.cx, list.cy, Array.from(list.indices.subarray(0, list.count))),
    onHarvestEvent: (events) => {
      for (let at = 0; at < events.count; at += 1) {
        options.harvest?.receiveEvent(events.indices[at] as number, events.codes[at] as number);
      }
    },
    onBar: (event) => options.arranged?.receive(event.seq, event.slots),
    onToolBelt: (event) => options.belt?.receive(event.seq, event.slots, event.selected),
    onCompanionState: (out, name) => options.companion?.receive(out, name),
    onLoot: (event) => {
      if (event.kind === "gained") {
        ground.gained(event.grounded, connection()?.view.entities ?? null);
        options.loot?.gained(event.items, event.lost, event.coin);
        options.sounds?.coinGained(event.coin);
      } else if (event.kind === "pickUpRefused") {
        ground.refused(event.reason, event.waitMs, event.handle);
      } else {
        options.chest?.receive(event);
      }
    },
    onEmotes: (sets) => options.emotes?.receive(sets),
    onEmojiShown: (emoji) => options.emoji?.(emoji),
    onMaintenance: (notice) => {
      options.maintenance?.receive(notice);
      const now = Date.now();
      options.progress({ type: "scheduled", countdown: countdownOf(notice, now), now });
    },
    onQueue: (queue) => {
      state.queued = true;
      options.progress({ type: "queued", place: queue.place, etaMs: queue.etaMs, length: queue.length, now: Date.now() });
    },
    onLeaveAnswer: (answer) => lifetime.leaveAnswered(answer),
    onFeedbackOffer: () => options.feedback?.offer(),
    onFeedbackAccess: (access, endsInMs) => options.feedback?.access(access, endsInMs),
    onModeration: (event) => options.moderation?.receive(event),
    onStaffBadgeOffer: (offer) => options.badge?.(offer),
    onVitals: (vitals) => {
      options.hud.setVitals(vitals);
      options.bar?.setVitals(vitals);
      fight.resources(vitals);
      const dead = (vitals.state & VitalsState.Dead) !== 0;
      options.run?.vitals(vitals.stamina, dead);
      fight.vitals(dead, host.now());
      if (dead) {
        options.death?.died(state.vitalsSeen);
        options.chest?.dismiss();
      } else {
        options.death?.stood();
      }
      state.vitalsSeen = true;
    },
    onRespawnOffer: (offer) => options.death?.offer(offer.readyTicks, offer.points, performance.now()),
    onShrines: (shrines) => {
      if (host.world()?.things.discoveries(shrines.handles, shrines.discovered, shrines.found, host.now()) === true) {
        announceDiscovery(shrines.name, shrines.xp, { toast: options.discoveries, chat: options.chat, sounds: options.sounds });
      }
    },
    onMapPlaces: (places) => options.worldMap?.receivePlaces(places),
    onExplored: (set) => options.worldMap?.receiveExplored(set),
    onPartyVitals: (vitals) => options.party?.receiveVitals(vitals),
    onPartyPositions: (positions) => options.partyPositions?.receive(positions),
    onThingEvent: (events) => {
      const things = host.world()?.things;
      if (things !== undefined) {
        playThingEvents(events, (handle) => things.pulse(handle, host.now()), sounds.thingPulsed);
      }
    },
    onLooks: (frame) => receiveLooks(looks, frame),
    onProjectiles: (projectiles) => {
      if (state.handle >= 0) {
        host.world()?.projectiles.receive(projectiles, connection()?.perf.smoothedRttMs ?? null);
      }
    },
    onPvpState: (pvp) => options.hud.setPvpState(pvp, ownNow()),
    onEffects: (effects) => {
      fight.effects(effects);
      options.hud.setMaxHealthPercent(frameMaxHealthPercent(PAGE_RULES, effects));
      options.bar?.setOwnEffects(effects, ownNow());
      options.death?.cost(deathCosts(PAGE_RULES, effects));
    },
    onTargetEffects: (effects) => options.bar?.setTargetEffects(effects, ownNow()),
    onPvpSwitch: (pvpSwitch) => {
      options.hud.setPvpSwitch(pvpSwitch, ownNow());
      const refusal = flagRefusalLine(pvpSwitch.note, options.hud.lastRequested);
      if (refusal !== null) {
        host.messageLine.say(refusal);
      }
    },
    onProgress: (progress) => {
      options.hud.setProgress(progress);
      const reached = levels.observe(progress.level);
      if (reached !== null) {
        options.levelUp?.show(reached);
        ownLevelUp(parts.vfx, state.handle);
        options.chat?.note(levelUpLine(reached));
      }
    },
    onCombat: (combat) => {
      gameReport.combatEvents += combat.count;
      if (state.handle >= 0) {
        fight.receive(combat, state.handle, host.now());
      }
    },
    onDiscarded: () => {
      gameReport.snapshotsDiscarded += 1;
      dropPendingLooks(looks);
    },
    onClose: lifetime.close,
    onSilent: lifetime.silent,
  };
}
